import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { useAuth } from '../../Constant/Auth';
import '../parentTables.css';
import SearchableSelect from '../../shared/SearchableSelect';

import {
  getIssuesByLibrary,
  addIssueRenewal,
  getIssueRenewalsByLibrary,
  getUserDataById,
  getErrorMessage
} from '../../api/api';

const ISSUE_STATUS_OPTIONS = [
  { value: 'issued', label: 'Issued' },
  { value: 'returned', label: 'Returned' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'lost', label: 'Lost' }
];

const getDefaultNewDue = (dueDateStr) => {
  const base = dueDateStr ? new Date(String(dueDateStr).slice(0, 10)) : new Date();
  base.setDate(base.getDate() + 14);
  return base.toISOString().slice(0, 10);
};

const ParentIssuedBooks = () => {
  const { user } = useAuth();

  const libraryId = user?.library_id || user?.libraryId;

  const [issues, setIssues]           = useState([]);
  const [loading, setLoading]         = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  // Renewal modal state
  const [renewIssue, setRenewIssue]   = useState(null);
  const [newDueDate, setNewDueDate]   = useState('');
  const [renewRemarks, setRenewRemarks] = useState('');
  const [renewing, setRenewing]       = useState(false);

  // Renewal history state
  const [allRenewals, setAllRenewals] = useState([]);
  const [historyIssue, setHistoryIssue] = useState(null);

  const fetchIssues = async () => {
    try {
      if (!libraryId) return;
      setLoading(true);

      // Resolve the actual members-table ID / membership ID via getUserDataById.
      // user.id is the auth/login ID, which differs from the members table ID that
      // the issues reference — matching on user.id returns nothing.
      const userData = user?.id ? await getUserDataById(user.id).catch(() => null) : null;

      const memberId =
        userData?.member_id || userData?.memberId ||
        userData?.library_member_id || userData?.libraryMemberId ||
        user?.member_id || user?.id || '';

      const membershipId =
        userData?.membership_id || userData?.membershipId ||
        userData?.active_membership_id || userData?.activeMembershipId ||
        user?.membership_id || '';

      const [data, renewalData] = await Promise.all([
        getIssuesByLibrary(libraryId),
        getIssueRenewalsByLibrary(libraryId).catch(() => [])
      ]);

      const myIssues = (data || []).filter((item) => {
        const itemMemberId = item.member_id || item.user_id || item.parent_id || '';
        const itemMembershipId = item.membership_id || '';

        return (
          (memberId && String(itemMemberId) === String(memberId)) ||
          (membershipId && String(itemMembershipId) === String(membershipId))
        );
      });

      setIssues(myIssues);
      setAllRenewals(Array.isArray(renewalData) ? renewalData : []);
    } catch (error) {
      console.error('Parent issued books error:', error);
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIssues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId, user?.id]);

  const openRenewModal = (issue) => {
    setRenewIssue(issue);
    setNewDueDate(getDefaultNewDue(issue.due_date));
    setRenewRemarks('');
  };

  const closeRenewModal = () => {
    setRenewIssue(null);
    setNewDueDate('');
    setRenewRemarks('');
  };

  const handleRenew = async (e) => {
    e.preventDefault();
    if (!newDueDate) {
      Swal.fire('Required', 'Please select a new due date', 'warning');
      return;
    }
    const oldDue = renewIssue.due_date ? String(renewIssue.due_date).slice(0, 10) : null;
    if (oldDue && newDueDate <= oldDue) {
      Swal.fire('Invalid Date', 'New due date must be after the current due date', 'warning');
      return;
    }

    try {
      setRenewing(true);
      await addIssueRenewal({
        issue_id: renewIssue.id,
        old_due_date: oldDue,
        new_due_date: newDueDate,
        renewed_by_staff_id: null,
        remarks: renewRemarks.trim() || null
      });
      await Swal.fire({
        icon: 'success',
        title: 'Renewed!',
        text: `Due date extended to ${newDueDate}`,
        timer: 2000,
        showConfirmButton: false
      });
      closeRenewModal();
      fetchIssues();
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setRenewing(false);
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'issued')   return 'bg-primary';
    if (status === 'returned') return 'bg-success';
    if (status === 'lost')     return 'bg-danger';
    if (status === 'overdue')  return 'bg-warning text-dark';
    return 'bg-secondary';
  };

  const isOverdue = (item) =>
    item.due_date && new Date(String(item.due_date).slice(0, 10)) < new Date();

  const canRenew = (item) => {
    const status = String(item.issue_status || '').toLowerCase();
    return status === 'issued' || status === 'overdue';
  };

  const filteredIssues = statusFilter
    ? issues.filter((item) => item.issue_status === statusFilter)
    : issues;

  return (
    <div className="container-fluid mt-3 pt-page">
      <div className="pt-page-header">
        <div className="pt-page-header__left">
          <span className="pt-page-header__icon">
            <i className="fas fa-book-open" />
          </span>
          <div>
            <h3 className="pt-page-header__title">My Issued Books</h3>
            <div className="pt-page-header__subtitle">View issued, returned and overdue books</div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm mt-3 table-full-card">
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-3 col-xs-6">
              <SearchableSelect
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                options={ISSUE_STATUS_OPTIONS}
                placeholder="All Status"
              />
            </div>
            <div className="col-md-2 col-xs-6">
              <button
                className="btn btn-outline-secondary"
                onClick={() => setStatusFilter('')}
              >
                Reset
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-4">Loading...</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Book</th>
                    <th>Copy</th>
                    <th>Issue Date</th>
                    <th>Due Date</th>
                    <th>Return Date</th>
                    <th>Renewals</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredIssues.length > 0 ? (
                    filteredIssues.map((item, index) => (
                      <tr key={item.id}>
                        <td>{index + 1}</td>
                        <td>
                          <strong>{item.title || item.book_title || '-'}</strong>
                          <div className="small text-muted">{item.author_name || '-'}</div>
                        </td>
                        <td>
                          {item.accession_no || '-'}
                          <div className="small text-muted">{item.barcode || '-'}</div>
                        </td>
                        <td>{item.issue_date ? String(item.issue_date).slice(0, 10) : '-'}</td>
                        <td>
                          {item.due_date ? String(item.due_date).slice(0, 10) : '-'}
                          {isOverdue(item) && item.issue_status !== 'returned' && (
                            <div>
                              <span className="badge bg-danger" style={{ fontSize: 10 }}>Overdue</span>
                            </div>
                          )}
                        </td>
                        <td>{item.return_date ? String(item.return_date).slice(0, 10) : '-'}</td>
                        <td className="text-center">
                          <span className="badge bg-light text-dark border">
                            {item.renew_count ?? 0}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${getStatusBadge(item.issue_status)}`}>
                            {item.issue_status || '-'}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {canRenew(item) && (
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => openRenewModal(item)}
                                title="Request renewal"
                              >
                                <i className="fas fa-redo me-1" />Renew
                              </button>
                            )}
                            {(item.renew_count ?? 0) > 0 && (
                              <button
                                className="btn btn-sm btn-outline-info"
                                onClick={() => setHistoryIssue(item)}
                                title="View renewal history"
                              >
                                <i className="fas fa-history me-1" />History
                              </button>
                            )}
                            {!canRenew(item) && (item.renew_count ?? 0) === 0 && (
                              <span className="text-muted small">—</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center text-muted py-4">
                        No issued books found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Renewal History Modal */}
      {historyIssue && (() => {
        const recs = allRenewals
          .filter((r) => String(r.issue_id) === String(historyIssue.id))
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        return (
          <div
            style={{
              position: 'fixed', inset: 0, zIndex: 1060,
              background: 'rgba(0,0,0,0.45)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
            }}
            onClick={() => setHistoryIssue(null)}
          >
            <div
              style={{
                background: '#fff', borderRadius: 14, width: '100%', maxWidth: 600,
                boxShadow: '0 20px 60px rgba(0,0,0,.25)', overflow: 'hidden'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 20px', borderBottom: '1px solid #e8edf5',
                background: 'linear-gradient(135deg,#0891b2,#0e7490)', color: '#fff'
              }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>
                  <i className="fas fa-history me-2" />Renewal History
                </span>
                <button
                  type="button"
                  onClick={() => setHistoryIssue(null)}
                  style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}
                >×</button>
              </div>

              {/* Book info strip */}
              <div style={{ padding: '12px 20px', background: '#f0f9ff', borderBottom: '1px solid #e0f2fe' }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#0c4a6e' }}>
                  {historyIssue.title || historyIssue.book_title || '-'}
                </div>
                <div style={{ fontSize: 12, color: '#0369a1', marginTop: 2, display: 'flex', gap: 16 }}>
                  <span><i className="fas fa-copy me-1" />{historyIssue.accession_no || historyIssue.barcode || '-'}</span>
                  <span><i className="fas fa-redo me-1" />{recs.length} renewal{recs.length !== 1 ? 's' : ''}</span>
                </div>
              </div>

              {/* Table */}
              <div style={{ maxHeight: 380, overflowY: 'auto', padding: '0 4px' }}>
                {recs.length === 0 ? (
                  <div style={{ padding: '36px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                    <i className="fas fa-history" style={{ fontSize: 26, display: 'block', marginBottom: 8 }} />
                    No renewal records found
                  </div>
                ) : (
                  <table className="table table-bordered table-hover align-middle mb-0" style={{ fontSize: 13 }}>
                    <thead style={{ position: 'sticky', top: 0, background: 'linear-gradient(135deg,#0891b2,#0e7490)', color: '#fff' }}>
                      <tr>
                        <th style={{ color: '#fff', border: 'none', padding: '10px 12px' }}>#</th>
                        <th style={{ color: '#fff', border: 'none', padding: '10px 12px' }}>Renewed On</th>
                        <th style={{ color: '#fff', border: 'none', padding: '10px 12px' }}>Old Due</th>
                        <th style={{ color: '#fff', border: 'none', padding: '10px 12px' }}>New Due</th>
                        <th style={{ color: '#fff', border: 'none', padding: '10px 12px' }}>By</th>
                        <th style={{ color: '#fff', border: 'none', padding: '10px 12px' }}>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recs.map((r, i) => (
                        <tr key={r.id || i}>
                          <td>{i + 1}</td>
                          <td>{r.created_at ? String(r.created_at).slice(0, 10) : '-'}</td>
                          <td>
                            {r.old_due_date ? (
                              <span className="badge bg-warning text-dark">
                                {String(r.old_due_date).slice(0, 10)}
                              </span>
                            ) : '-'}
                          </td>
                          <td>
                            {r.new_due_date ? (
                              <span className="badge bg-success">
                                {String(r.new_due_date).slice(0, 10)}
                              </span>
                            ) : '-'}
                          </td>
                          <td>
                            {r.renewed_by_staff_id
                              ? <span className="badge bg-primary">Staff</span>
                              : <span className="badge bg-info text-dark">Self</span>}
                          </td>
                          <td className="text-muted">{r.remarks || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Footer */}
              <div style={{
                padding: '10px 20px', borderTop: '1px solid #e8edf5',
                background: '#f8fafc', display: 'flex', justifyContent: 'flex-end'
              }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setHistoryIssue(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Renewal Modal */}
      {renewIssue && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1060,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
          }}
          onClick={closeRenewModal}
        >
          <div
            style={{
              background: '#fff', borderRadius: 14, width: '100%', maxWidth: 460,
              boxShadow: '0 20px 60px rgba(0,0,0,.25)', overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 20px', borderBottom: '1px solid #e8edf5',
              background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', color: '#fff'
            }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>
                <i className="fas fa-redo me-2" />Renew Book
              </span>
              <button
                type="button"
                onClick={closeRenewModal}
                disabled={renewing}
                style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}
              >×</button>
            </div>

            {/* Book info */}
            <div style={{ padding: '16px 20px', background: '#f8fafc', borderBottom: '1px solid #e8edf5' }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 4 }}>
                {renewIssue.title || renewIssue.book_title || '-'}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <span><i className="fas fa-copy me-1" />{renewIssue.accession_no || renewIssue.barcode || '-'}</span>
                <span><i className="fas fa-calendar me-1" />Current due: <strong style={{ color: isOverdue(renewIssue) ? '#dc2626' : '#1e293b' }}>
                  {renewIssue.due_date ? String(renewIssue.due_date).slice(0, 10) : '-'}
                </strong></span>
                <span><i className="fas fa-redo me-1" />Renewals so far: <strong>{renewIssue.renew_count ?? 0}</strong></span>
              </div>
              {isOverdue(renewIssue) && (
                <div style={{
                  marginTop: 8, padding: '6px 10px', borderRadius: 8,
                  background: '#fef2f2', border: '1px solid #fecaca',
                  fontSize: 12, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 6
                }}>
                  <i className="fas fa-exclamation-triangle" />
                  This book is overdue. Renewal may be subject to librarian approval.
                </div>
              )}
            </div>

            {/* Form */}
            <form onSubmit={handleRenew} style={{ padding: '20px' }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>
                  New Due Date *
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={newDueDate}
                  min={renewIssue.due_date ? String(renewIssue.due_date).slice(0, 10) : undefined}
                  onChange={(e) => setNewDueDate(e.target.value)}
                  required
                  disabled={renewing}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>
                  Remarks <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span>
                </label>
                <textarea
                  className="form-control"
                  rows={2}
                  placeholder="Reason for renewal..."
                  value={renewRemarks}
                  onChange={(e) => setRenewRemarks(e.target.value)}
                  disabled={renewing}
                />
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={closeRenewModal}
                  disabled={renewing}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={renewing || !newDueDate}
                >
                  {renewing ? (
                    <><span className="spinner-border spinner-border-sm me-1" />Renewing...</>
                  ) : (
                    <><i className="fas fa-redo me-1" />Confirm Renewal</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentIssuedBooks;
